import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
  Badge,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Home as HomeIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  Menu as MenuIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  LocalOffer as CouponIcon,
  ReceiptLong as OrdersIcon,
  Campaign as AdsIcon,
  PictureAsPdf as ResumeIcon,
  VerifiedUser as ServicesIcon,
  LocalLibrary as CoursesIcon,
  Forum as ForumIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 240;

// User menu items
const userMenuItems = [
  { text: 'User Dashboard', icon: <PersonIcon />, path: '/user/dashboard', description: 'Job alerts, coupons & ads' },
  { text: 'Job Alerts', icon: <WorkIcon />, path: '/user/job-alerts', description: 'Browse job opportunities' },
  { text: 'Coupons', icon: <CouponIcon />, path: '/user/coupons', description: 'Discounts & offers' },
  { text: 'Shop Orders', icon: <OrdersIcon />, path: '/user/orders', description: 'Orders sent to shops' },
  { text: 'Advertisements', icon: <AdsIcon />, path: '/user/ads', description: 'View ads' },
  { text: 'Application Chat', icon: <ForumIcon />, path: '/user/confirmations', description: 'Confirm applications' },
  { text: 'Career Profile', icon: <PersonIcon />, path: '/user/career-profile', description: 'Complete your profile' },
  { text: 'Resume Generator', icon: <ResumeIcon />, path: '/user/resume', description: 'Generate PDF resume' },
  { text: 'Services', icon: <ServicesIcon />, path: '/services', description: 'KNLS government services' },
  { text: 'Courses', icon: <CoursesIcon />, path: '/courses', description: 'KNLS computer courses' },
];

const UserLayout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    const redirectPath = logout();
    navigate(redirectPath);
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  // Get page title based on route
  const getPageTitle = () => {
    const routeTitles = {
      '/user/dashboard': 'User Dashboard',
      '/user/job-alerts': 'Job Alerts',
      '/user/coupons': 'Coupons',
      '/user/orders': 'Shop Orders',
      '/user/redeem': 'Redeem coupon',
      '/user/ads': 'Advertisements',
      '/user/confirmations': 'Application Chat',
      '/user/career-profile': 'Career Profile',
      '/user/resume': 'Resume Generator',
      '/user/profile': 'Profile',
    };
    return routeTitles[location.pathname] || 'Dashboard';
  };

  // Drawer content
  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 35%, #111827 100%)',
        backgroundColor: '#0f172a',
        backgroundAttachment: 'fixed',
        color: '#ffffff !important',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        transform: 'translateZ(0)',
        '& *::selection': {
          backgroundColor: '#3b82f6',
          color: '#ffffff',
        },
        '& *::-moz-selection': {
          backgroundColor: '#3b82f6',
          color: '#ffffff',
        },
        '& *': {
          color: '#ffffff !important',
          WebkitTextFillColor: '#ffffff !important',
        },
      }}
    >
      <Toolbar
        sx={{
          minHeight: 72,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40, fontWeight: 700 }}>L</Avatar>
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{
              color: '#ffffff !important',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
            }}
          >
            LOCSY User
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.8) !important',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
            }}
          >
            User Dashboard
          </Typography>
        </Box>
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mb: 1 }} />

      <List sx={{ px: 1.5, flexGrow: 1 }}>
        {userMenuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={isActive}
                onClick={() => {
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1.2,
                  transition: 'all 0.25s ease',
                  backdropFilter: isActive ? 'blur(4px)' : 'none',
                  cursor: 'pointer',
                  color: '#ffffff !important',
                  textDecoration: 'none',
                  WebkitFontSmoothing: 'antialiased',
                  MozOsxFontSmoothing: 'grayscale',
                  '&, & *': {
                    color: '#ffffff !important',
                  },
                  '&::selection, & *::selection': {
                    backgroundColor: '#3b82f6 !important',
                    color: '#ffffff !important',
                  },
                  '&::-moz-selection, & *::-moz-selection': {
                    backgroundColor: '#3b82f6 !important',
                    color: '#ffffff !important',
                  },
                  '&.Mui-selected': {
                    background: 'linear-gradient(90deg, rgba(59,130,246,0.25), rgba(59,130,246,0.1))',
                    boxShadow: 'inset 0 0 0 1px rgba(59,130,246,0.35)',
                    color: '#ffffff !important',
                    '&:hover': {
                      background: 'linear-gradient(90deg, rgba(59,130,246,0.35), rgba(59,130,246,0.15))',
                      color: '#ffffff !important',
                    },
                    '& .MuiListItemIcon-root': {
                      color: '#60A5FA !important',
                    },
                    '& .MuiTypography-root': {
                      color: '#ffffff !important',
                    },
                  },
                  '&:hover': {
                    background: 'rgba(255,255,255,0.12)',
                    color: '#ffffff !important',
                  },
                  '& .MuiListItemIcon-root': {
                    color: isActive ? '#60A5FA !important' : 'rgba(255,255,255,0.85) !important',
                  },
                  '& .MuiTypography-root': {
                    color: '#ffffff !important',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? '#60A5FA !important' : 'rgba(255,255,255,0.8) !important',
                    minWidth: 36,
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        color: '#ffffff !important',
                        WebkitFontSmoothing: 'antialiased',
                        MozOsxFontSmoothing: 'grayscale',
                      }}
                    >
                      {item.text}
                    </Typography>
                  }
                  secondary={
                    item.description ? (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(255,255,255,0.7) !important',
                          WebkitFontSmoothing: 'antialiased',
                          MozOsxFontSmoothing: 'grayscale',
                        }}
                      >
                        {item.description}
                      </Typography>
                    ) : null
                  }
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 0.5 }}>
          © {new Date().getFullYear()} LOCSY • User Portal
        </Typography>
        <Link
          to="/privacy-policy"
          style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', textDecoration: 'underline' }}
        >
          Privacy Policy
        </Link>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          background: 'linear-gradient(90deg, #0F172A 0%, #1E3A8A 45%, #2563EB 100%)',
          boxShadow: '0 10px 30px rgba(15,23,42,0.25)',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: 72 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Tooltip title="Home - Go to Dashboard">
            <Button
              color="inherit"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/user/dashboard')}
              sx={{
                mr: 2,
                textTransform: 'none',
                fontWeight: 600,
                display: { xs: 'none', sm: 'inline-flex' },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Home
            </Button>
          </Tooltip>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, minWidth: 0, fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            {getPageTitle()}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2 } }}>
            <IconButton color="inherit">
              <Badge badgeContent={0} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
              }}
              onClick={handleMenuOpen}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {user?.full_name?.charAt(0) || 'U'}
              </Avatar>
              <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {user?.full_name || 'User'}
              </Typography>
            </Box>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
          >
            <MenuItem onClick={() => handleNavigate('/user/profile')}>
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 35%, #111827 100%) !important',
              backgroundColor: '#0f172a !important',
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 35%, #111827 100%) !important',
              backgroundColor: '#0f172a !important',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 0, sm: 2, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          pt: 9,
          backgroundColor: 'background.default',
          minHeight: '100vh',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default UserLayout;

